import { Permissions } from '../auth/permissions'
import type { NavIconName } from './NavIcon'

export type NavItem = {
  to: string
  label: string
  icon: NavIconName
  /** Hidden when the user lacks this. A courtesy — the server is the control. */
  permission?: string
  /** Shown when the user holds any of these — a module several roles use differently. */
  anyOf?: string[]
  /** Not yet built; routed to a stub that says so rather than a broken page. */
  stub?: boolean
  /** Where it is current, when that is not simply "under `to`". */
  current?: (pathname: string) => boolean
  /** A destination and name that depend on what this person can open. */
  resolve?: (can: (permission: string) => boolean) => { to: string; label: string } | null
}

/** Sales' pages other than the till, in the order their tabs show them. */
const salesDesks = [
  { to: '/sales/register', label: 'Sales Register', permission: Permissions.Sales.ViewRegister },
  { to: '/sales/support', label: 'Support', permission: Permissions.Support.View },
  { to: '/sales/credit-notes', label: 'Credit notes', permission: Permissions.Sales.CreateCreditNote },
  { to: '/sales/credit-notes', label: 'Credit notes', permission: Permissions.Sales.ApproveCreditNote },
]

/**
 * The working day from the top: the till second, as DP Light's own mockup has it
 * (D-115), then the records, stock, money and delivery. Staff learn positions rather
 * than labels, so the order changes only on purpose.
 */
export const navigation: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: 'dashboard', permission: Permissions.Dashboard.View },
  { to: '/sales', label: 'POS', icon: 'pos', permission: Permissions.Sales.PosSell, current: (path) => path === '/sales' },
  // The register and the after-sales desk. Named for the first of them this person
  // can open: a cashier, who has no register, sees Support.
  {
    to: '/sales/register',
    label: 'Sales Register',
    icon: 'sales',
    anyOf: salesDesks.map((d) => d.permission),
    current: (path) => path.startsWith('/sales/'),
    resolve: (can) => {
      const first = salesDesks.find((d) => can(d.permission))
      return first ? { to: first.to, label: first.label } : null
    },
  },
  // M18: the records, their 360 and journey, leads and loyalty.
  { to: '/customers', label: 'Customers', icon: 'customers', anyOf: [Permissions.Customer.View, Permissions.Crm.ViewLeads] },
  // D-125: campaigns, automatic messages, segments, the log — a module of its own.
  { to: '/messages', label: 'Message Center', icon: 'messages', permission: Permissions.Messaging.View },
  { to: '/products', label: 'Products', icon: 'products', permission: Permissions.Product.View },
  { to: '/purchases', label: 'Purchases', icon: 'purchases', permission: Permissions.Purchasing.View },
  { to: '/warehouse', label: 'Warehouse', icon: 'warehouse', permission: Permissions.Warehouse.ViewStock },
  {
    to: '/finance',
    label: 'Finance',
    icon: 'finance',
    anyOf: [Permissions.Finance.ViewAccounts, Permissions.Finance.PettyCashSpend, Permissions.Finance.PettyCashApprove, Permissions.Finance.ViewReports, Permissions.Finance.RecordReceipt, Permissions.Finance.Reconcile],
  },
  // M19: the board, the rider's run, riders, the SLA overview and inbound shipments.
  { to: '/logistics', label: 'Logistics', icon: 'delivery', anyOf: [Permissions.Delivery.ViewBoard, Permissions.Delivery.CompleteOwn, Permissions.Logistics.ViewShipments] },
  { to: '/advisor', label: 'AI Advisor', icon: 'advisor', permission: Permissions.Reports.View },
  {
    to: '/users',
    label: 'User Master',
    icon: 'users',
    anyOf: [Permissions.Administration.ManageUsers, Permissions.Administration.ManageRoles],
    resolve: (can) => ({ to: can(Permissions.Administration.ManageUsers) ? '/users' : '/users/roles', label: 'User Master' }),
  },
  { to: '/settings', label: 'Settings', icon: 'settings', permission: Permissions.Administration.ManageSettings },
]

/** The menu as this person sees it: what they may open, named for where it goes. */
export function visibleNavigation(can: (permission: string) => boolean): NavItem[] {
  return navigation.flatMap((item) => {
    const allowed = item.anyOf ? item.anyOf.some((p) => can(p)) : !item.permission || can(item.permission)
    if (!allowed) return []
    const resolved = item.resolve?.(can)
    if (item.resolve && !resolved) return []
    return [resolved ? { ...item, ...resolved } : item]
  })
}

/** The menu item for a page, for the header's title. */
export function currentItem(pathname: string): NavItem | undefined {
  return navigation.find((item) => (item.current ? item.current(pathname) : item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)))
}
